import React from "react";

const Terms: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">
            Terms of Service
          </h1>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-6">
              <strong>Last updated:</strong> 20/11/2025
            </p>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                1. Acceptance of Terms
              </h2>
              <p className="text-gray-700 mb-4">
                By accessing and using the Experience mobile application
                ("App"), you agree to be bound by this agreement. If you do not
                accept these Terms, you must not use the App.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                2. Acknowledgement
              </h2>
              <p className="text-gray-700 mb-4">
                This agreement is concluded between you and the developer only,
                not with Apple. The developer, not Apple, is solely responsible
                for the App and its content. These Terms must not conflict with
                the Usage Rules in the Apple Media Services Terms and
                Conditions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                3. Scope of License
              </h2>
              <p className="text-gray-700 mb-4">
                A non-transferable, non-exclusive license is granted to use the
                App on any Apple-branded device you own or control, in
                accordance with the Usage Rules. The App may also be accessed by
                other accounts associated with your Apple ID through Family
                Sharing or volume purchasing. You may not transfer,
                redistribute, or sublicense the App.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                4. Description of Service
              </h2>
              <p className="text-gray-700 mb-4">
                Experience is a feeling-tracking and wellness application
                providing tools to:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                <li>Track and analyze experience patterns</li>
                <li>Monitor flow state and productivity indicators</li>
                <li>Collaborate with team through shared insights</li>
                <li>Access personalized analytics</li>
                <li>Receive push notifications for check-ins and reminders</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                5. User Accounts and Registration
              </h2>
              <p className="text-gray-700 mb-4">
                App use require registration. You agree to:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                <li>Provide accurate and current information</li>
                <li>
                  Take responsibility for all activities under your account
                </li>
                <li>Notify support of unauthorized access</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                6. Data Collection and Privacy
              </h2>
              <p className="text-gray-700 mb-4">
                Personal data is processed as described in the Privacy Policy.
                This includes:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                <li>Feeling entries</li>
                <li>Server-stored data</li>
                <li>Notification tokens</li>
                <li>Team-related shared data</li>
              </ul>
              <p className="text-gray-700 mb-4">
                Use of the App constitutes consent to these processing
                operations.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                7. Maintenance and Support
              </h2>
              <p className="text-gray-700 mb-4">
                The developer is solely responsible for providing maintenance
                and support for the App. Apple has no obligation whatsoever to
                provide support or maintenance services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                8. Warranty
              </h2>
              <p className="text-gray-700 mb-4">
                To the extent a warranty applies, if the App fails to conform to
                such warranty, you may notify Apple for a refund of the purchase
                price (if any). To the maximum extent permitted by law, Apple
                will have no other warranty obligations. All other claims,
                losses, damages, or expenses related to warranty will be the
                responsibility of the developer.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                9. Product Claims
              </h2>
              <p className="text-gray-700 mb-4">
                The developer is responsible for addressing all claims by you or
                any third party relating to the App or your possession and/or
                use of it, including:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                <li>Product liability claims</li>
                <li>Legal or regulatory compliance claims</li>
                <li>Consumer protection, privacy, or similar claims</li>
              </ul>
              <p className="text-gray-700 mb-4">
                Apple has no responsibility for such claims.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                10. Intellectual Property Infringement
              </h2>
              <p className="text-gray-700 mb-4">
                If any third party claims that the App infringes their
                intellectual property rights, the developer is solely
                responsible for the investigation, defense, settlement, and
                discharge of such claims.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                11. Legal Compliance
              </h2>
              <p className="text-gray-700 mb-4">
                You represent and warrant that:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                <li>
                  You are not located in a country subject to a U.S. Government
                  embargo
                </li>
                <li>
                  You are not listed on any U.S. Government list of prohibited
                  or restricted parties
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                12. Push Notifications
              </h2>
              <p className="text-gray-700 mb-4">
                The App may send notifications for check-ins, reminders, and
                updates. You may opt in during setup or disable notifications in
                system settings.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                13. Team Features
              </h2>
              <p className="text-gray-700 mb-4">
                When using team features, you agree that:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                <li>Team leads must respect member privacy</li>
                <li>Member feeling data is shared with lead</li>
                <li>
                  Team data must be used only for experience/wellness tracking
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                14. Prohibited Uses
              </h2>
              <p className="text-gray-700 mb-4">You may not use the App to:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 ml-4">
                <li>Engage in unlawful behavior</li>
                <li>Violate regulations</li>
                <li>Infringe intellectual property</li>
                <li>Harass, abuse, or defame others</li>
                <li>Submit misleading information</li>
                <li>Upload malicious code</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                15. Intellectual Property Rights
              </h2>
              <p className="text-gray-700 mb-4">
                The App, services, and all related materials are the exclusive
                property of the developer and licensors. All rights reserved.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                16. Third-Party Terms
              </h2>
              <p className="text-gray-700 mb-4">
                Your use of the App must comply with applicable third-party
                agreements, including wireless data service agreements.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                17. Termination
              </h2>
              <p className="text-gray-700 mb-4">
                Account access may be suspended or terminated immediately for
                any violation of these Terms or at the developer's discretion.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                18. Disclaimer
              </h2>
              <p className="text-gray-700 mb-4">
                The App is provided "as is" and "as available." All warranties
                and conditions are disclaimed to the fullest extent permitted by
                law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                19. Limitation of Liability
              </h2>
              <p className="text-gray-700 mb-4">
                To the extent not prohibited by law, the developer is not liable
                for indirect, incidental, or consequential damages arising from
                use of the App.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                20. Third-Party Beneficiary
              </h2>
              <p className="text-gray-700 mb-4">
                Apple and its subsidiaries are third-party beneficiaries of
                these Terms. Upon your acceptance of this agreement, Apple has
                the right to enforce these Terms against you as a third-party
                beneficiary.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                21. Changes to Terms
              </h2>
              <p className="text-gray-700 mb-4">
                These Terms may be updated at any time. Material changes will be
                announced at least 7 days before taking effect.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                22. Developer Information
              </h2>
              <p className="text-gray-700 mb-4">Developer: Ruslan Eskov</p>
              <div className="bg-gray-100 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>Address:</strong> Turu 8, 51004 Tartu, Estonia
                  <br />
                  <strong>Phone:</strong> +372 5845 4407
                  <br />
                  <strong>Email:</strong> support@experienceapp.io
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
